import type { Probe, ProbeResult } from '../engine/types';
import { ok } from '../engine/runner';
import { registerProbe } from '../engine/registry';

/**
 * On-device AI availability.
 *
 * Chrome's built-in AI APIs (Summarizer, Translator, LanguageDetector, and the
 * older window.ai) are gated by hardware, OS version and a downloaded model.
 * A real browser either has them consistently present or consistently absent;
 * a headless or VM image that shims some AI globals but not others — or that
 * exposes the API without a working model download — is a useful tell. The
 * reference site collects this as their `ai` signal.
 *
 * We only probe presence; we never invoke a model or download weights.
 */
export const aiProbe: Probe = {
  id: 'ai',
  label: 'On-device AI',
  layer: 'browser',
  tier: 'client',
  category: 'browser',
  description: 'Built-in AI API presence (Summarizer, Translator, LanguageDetector)',
  budgetMs: 1000,

  async run(): Promise<ProbeResult> {
    const w = window as unknown as Record<string, unknown>;

    const presence = {
      // Modern per-task APIs.
      Summarizer: 'Summarizer' in w,
      Translator: 'Translator' in w,
      LanguageDetector: 'LanguageDetector' in w,
      // Legacy combined API.
      ai: 'ai' in w,
      opr: 'opr' in w,
      // Writer/Rewriter (Proofreader API).
      Proofreader: 'Proofreader' in w,
    };

    // A mixed state (some present, some absent) is suspicious, because the
    // APIs ship together within a browser version. Report the count either way.
    const presentCount = Object.values(presence).filter(Boolean).length;

    // Probe availability() where it exists, WITHOUT triggering a model
    // download. availability() is the cheap, non-downloading check.
    const availability: Record<string, string> = {};
    const Summarizer = (w as { Summarizer?: { availability?: (o?: unknown) => Promise<string> } }).Summarizer;
    if (typeof Summarizer?.availability === 'function') {
      try {
        availability['summarizer'] = await Summarizer.availability({
          expectedInputLanguages: [navigator.language],
        });
      } catch {
        availability['summarizer'] = 'error';
      }
    }
    const LanguageDetector = (
      w as { LanguageDetector?: { availability?: () => Promise<string> } }
    ).LanguageDetector;
    if (typeof LanguageDetector?.availability === 'function') {
      try {
        availability['languageDetector'] = await LanguageDetector.availability();
      } catch {
        availability['languageDetector'] = 'error';
      }
    }

    const anyPresent = presentCount > 0;

    return {
      signals: {
        supported: ok(anyPresent, 'derived'),
        presence: ok(presence, 'client'),
        presentCount: ok(presentCount, 'client'),
        // A partial set of these APIs coexisting is the interesting case.
        partialApiSet: ok(presentCount > 0 && presentCount < Object.keys(presence).length, 'derived'),
        availability: ok(availability, 'client'),
      },
      // NO part contribution at all. The built-in AI APIs (Summarizer,
      // Translator, LanguageDetector) are registered ASYNCHRONOUSLY by the
      // browser, so a page that samples them early can see 0 and a moment
      // later see 3 for the very same browser. Hashing that count made
      // browserFeatures drift between two scans of one browser, which the
      // stability suite caught. The value is still reported as a signal; it
      // simply must not perturb the fingerprint. Only synchronous, always-
      // present properties may enter a part.
    };
  },
};

registerProbe(aiProbe);
